/**
 * EG Lore Detail V2
 * -----------------
 * Wires the lore cards to their native <dialog> popups (the theme's
 * <dialog-component> owns focus trap, scroll lock and ESC / backdrop close)
 * and layers the small immersion details on top:
 *
 *   - one dialog open at a time, aria-expanded kept in sync on the card;
 *   - deep links: `#story`, `#profile`, `#companion` open the
 *     matching card, the browser back button closes it;
 *   - lazy stylesheet and module for cards that declare
 *     `data-lore-stylesheet` / `data-lore-script` (the Story reader), warmed
 *     on hover / focus, awaited before the dialog opens;
 *   - a soft accent glow that follows the pointer over the stage on
 *     hover-capable devices (transform-only, disabled with reduced motion);
 *   - theme editor: selecting a block (or a nested dossier block) opens the
 *     dialog it lives in;
 *   - slim footer bar: flags the legal-links list while it overflows so the
 *     CSS can fade its right edge (dropped once scrolled to the end);
 *   - mobile edge tabs: shrinks a tab title that would not fit on one line
 *     (sets --eg-lv2-tab-fit on the card), so "Companion" in a wide display
 *     font never breaks mid-word;
 *   - 3D character trial: with `?c=machma3d` in the URL and a model URL on
 *     the portrait body, lazily imports eg-lore-v2-model.js, which swaps the
 *     portrait for a scroll-turned GLB on the same stage.
 */

/** Query parameter that switches the 3D character trial on: `?c=machma3d`. */
const MODEL_TRIAL_PARAM = 'c';
const MODEL_TRIAL_VALUE = 'machma3d';

const stylesheetRequests = new Map();
const scriptRequests = new Map();

/**
 * Imports a module once and shares the promise across cards / section
 * instances. Resolves to false (and forgets the URL so it can be retried)
 * when the import fails.
 * @param {string} url
 * @returns {Promise<boolean>}
 */
function loadScript(url) {
  if (!url) return Promise.resolve(true);
  if (scriptRequests.has(url)) return scriptRequests.get(url);
  const request = import(url)
    .then(() => true)
    .catch(() => {
      scriptRequests.delete(url);
      return false;
    });
  scriptRequests.set(url, request);
  return request;
}

/**
 * Loads a stylesheet once and shares the promise across cards / section
 * instances. Resolves to false (and forgets the URL so it can be retried)
 * when the request fails or times out.
 * @param {string} url
 * @returns {Promise<boolean>}
 */
function loadStylesheet(url) {
  if (!url) return Promise.resolve(true);
  if (stylesheetRequests.has(url)) return stylesheetRequests.get(url);

  const request = new Promise((resolve) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = url;
    const finish = (loaded) => {
      clearTimeout(timeout);
      link.onload = link.onerror = null;
      if (!loaded) {
        link.remove();
        stylesheetRequests.delete(url);
      }
      resolve(loaded);
    };
    const timeout = setTimeout(() => finish(false), 15000);
    link.onload = () => finish(true);
    link.onerror = () => finish(false);
    document.head.append(link);
  });

  stylesheetRequests.set(url, request);
  return request;
}

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const constrainedNetwork = () => {
  const connection = navigator.connection;
  return Boolean(connection?.saveData) || /^(slow-)?2g$/.test(connection?.effectiveType || '');
};

class EgLoreDetailV2 extends HTMLElement {
  #initialized = false;
  #cards = [];
  #openRequest = 0;
  #pointerCleanup = null;
  #barCleanup = null;
  #tabsCleanup = null;
  #modelCleanup = null;
  #modelRequest = 0;

  connectedCallback() {
    this.#initPointerGlow();
    this.#initBar();
    this.#initTabTitles();
    this.#initModel();
    if (this.#initialized) {
      // Re-attached (theme editor reorder): the global listeners were dropped
      // in disconnectedCallback, the cards are still wired.
      this.#attachGlobalListeners();
      return;
    }
    this.#initialized = true;

    const start = () => this.#init();
    if (window.customElements && typeof window.customElements.whenDefined === 'function') {
      window.customElements.whenDefined('dialog-component').then(start);
    } else {
      start();
    }
  }

  disconnectedCallback() {
    ++this.#openRequest;
    window.removeEventListener('popstate', this.#handlePopState);
    document.removeEventListener('shopify:block:select', this.#handleBlockSelect);
    if (this.#pointerCleanup) {
      this.#pointerCleanup();
      this.#pointerCleanup = null;
    }
    if (this.#barCleanup) {
      this.#barCleanup();
      this.#barCleanup = null;
    }
    if (this.#tabsCleanup) {
      this.#tabsCleanup();
      this.#tabsCleanup = null;
    }
    ++this.#modelRequest;
    if (this.#modelCleanup) {
      this.#modelCleanup();
      this.#modelCleanup = null;
    }
  }

  /**
   * 3D character trial. Only with `?c=machma3d` in the URL and a portrait
   * body that carries a model URL; the flat portrait stays in place until the
   * module has drawn its first frame, and remains the fallback for every
   * failure (no WebGL 2, download error, saved-data connections).
   */
  #initModel() {
    if (this.#modelCleanup) return;
    const body = this.querySelector('.eg-lv2__portrait-body[data-model]');
    if (!(body instanceof HTMLElement)) return;

    let params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch {
      return;
    }
    if (params.get(MODEL_TRIAL_PARAM) !== MODEL_TRIAL_VALUE) return;
    if (constrainedNetwork()) return;

    const { model, modelScript, modelLib, modelLabel } = body.dataset;
    if (!model || !modelScript || !modelLib) return;

    const request = ++this.#modelRequest;
    import(modelScript)
      .then((module) => module.default(body, { modelUrl: model, libUrl: modelLib, label: modelLabel }))
      .then((cleanup) => {
        if (typeof cleanup !== 'function') return;
        if (request !== this.#modelRequest || !this.isConnected) {
          cleanup();
          return;
        }
        this.#modelCleanup = cleanup;
      })
      .catch(() => {
        /* the portrait image stays */
      });
  }

  /**
   * Mobile edge layout: the tab titles are single-line; a title wider than
   * its tab gets a shrink factor (never below 0.72) so it fits on one line.
   */
  #initTabTitles() {
    if (this.#tabsCleanup) {
      this.#tabsCleanup();
      this.#tabsCleanup = null;
    }
    if (!this.classList.contains('eg-lv2--mobile-edge')) return;
    const titles = Array.from(this.querySelectorAll('.eg-lv2-card__title'));
    if (titles.length === 0) return;
    const media = window.matchMedia('(max-width: 749px)');

    const fit = () => {
      for (const title of titles) {
        const card = title.closest('.eg-lv2-card');
        if (!(card instanceof HTMLElement)) continue;
        card.style.removeProperty('--eg-lv2-tab-fit');
        if (!media.matches) continue;
        const available = title.clientWidth;
        const needed = title.scrollWidth;
        if (available > 0 && needed > available) {
          const ratio = Math.max(0.72, Math.floor((available / needed) * 100 - 1) / 100);
          card.style.setProperty('--eg-lv2-tab-fit', String(ratio));
        }
      }
    };
    let frame = 0;
    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        fit();
      });
    };

    fit();
    document.fonts?.ready?.then(fit).catch(() => {});
    window.addEventListener('resize', schedule, { passive: true });
    media.addEventListener?.('change', schedule);
    this.#tabsCleanup = () => {
      window.removeEventListener('resize', schedule);
      media.removeEventListener?.('change', schedule);
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
    };
  }

  /** Marks the footer legal list as clipped while it can still scroll right. */
  #initBar() {
    if (this.#barCleanup) {
      this.#barCleanup();
      this.#barCleanup = null;
    }
    const list = this.querySelector('.eg-lv2__bar-list');
    if (!list) return;

    const update = () => {
      const clipped = list.scrollWidth - list.clientWidth - list.scrollLeft > 2;
      list.classList.toggle('is-clipped', clipped);
    };
    let frame = 0;
    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        update();
      });
    };

    list.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    update();
    this.#barCleanup = () => {
      list.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
    };
  }

  #init() {
    this.#cards = Array.from(this.querySelectorAll('[data-lv2-card]'));
    const usedSlugs = new Map();

    for (const card of this.#cards) {
      // Stable, human-readable hash per card; duplicates get a running suffix.
      const base = (card.dataset.lv2Slug || 'card').toLowerCase();
      const count = (usedSlugs.get(base) || 0) + 1;
      usedSlugs.set(base, count);
      card.dataset.lv2Hash = count > 1 ? `${base}-${count}` : base;

      const trigger = card.querySelector('[data-lv2-trigger]');
      const host = card.querySelector('dialog-component');
      if (!trigger || !host) continue;

      const warm = () => {
        if (constrainedNetwork()) return;
        loadStylesheet(this.#stylesheetFor(card));
        loadScript(this.#scriptFor(card));
      };
      trigger.addEventListener('pointerenter', warm, { passive: true });
      trigger.addEventListener('focus', warm);
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        this.#openCard(card);
      });

      host.addEventListener('dialog:close', () => this.#handleClose(card));
    }

    this.#applyHash();
    this.#attachGlobalListeners();
  }

  #attachGlobalListeners() {
    window.addEventListener('popstate', this.#handlePopState);
    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', this.#handleBlockSelect);
    }
  }

  /** @param {HTMLElement} card */
  #stylesheetFor(card) {
    const dialog = card.querySelector('[data-lore-stylesheet]');
    return dialog instanceof HTMLElement ? dialog.dataset.loreStylesheet || '' : '';
  }

  /** @param {HTMLElement} card */
  #scriptFor(card) {
    if (card.dataset.loreScript) return card.dataset.loreScript;
    const holder = card.querySelector('[data-lore-script]');
    return holder instanceof HTMLElement ? holder.dataset.loreScript || '' : '';
  }

  #handlePopState = () => {
    this.#applyHash();
  };

  /** @param {Event} event */
  #handleBlockSelect = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !this.contains(target)) return;
    const card = target.closest('[data-lv2-card]');
    if (card instanceof HTMLElement) this.#openCard(card, true);
  };

  #applyHash() {
    let id = '';
    try {
      id = decodeURIComponent(window.location.hash.slice(1));
    } catch {
      return;
    }
    const target = id ? this.#cards.find((card) => card.dataset.lv2Hash === id) : null;
    if (target) {
      this.#openCard(target, true);
    } else {
      this.#closeAll();
    }
  }

  /**
   * @param {HTMLElement} card
   * @param {boolean} [skipPushState]
   */
  async #openCard(card, skipPushState = false) {
    const request = ++this.#openRequest;
    const trigger = card.querySelector('[data-lv2-trigger]');
    const host = card.querySelector('dialog-component');
    if (!host || typeof host.showDialog !== 'function') return;

    const dialog = host.querySelector('dialog');
    if (!dialog || dialog.open) {
      trigger?.setAttribute('aria-expanded', 'true');
      return;
    }

    trigger?.setAttribute('aria-busy', 'true');
    // The stylesheet gates the open (an unstyled reader is unusable); the
    // module is awaited so its element upgrades before the dialog shows, but
    // a failed import still opens the plain article.
    const [loaded] = await Promise.all([
      loadStylesheet(this.#stylesheetFor(card)),
      loadScript(this.#scriptFor(card)),
    ]);
    trigger?.removeAttribute('aria-busy');
    if (!loaded || request !== this.#openRequest || !this.isConnected) return;

    await this.#closeAllExcept(card);
    if (request !== this.#openRequest || !this.isConnected) return;

    // The native dialog returns focus to the trigger on close — also for deep links.
    trigger?.focus({ preventScroll: true });
    const scroller = dialog.querySelector('[data-lv2-scroll]');
    if (scroller) scroller.scrollTop = 0;
    host.showDialog();
    trigger?.setAttribute('aria-expanded', 'true');

    const hash = card.dataset.lv2Hash;
    if (!skipPushState && hash) {
      const url = `${window.location.pathname}${window.location.search}#${encodeURIComponent(hash)}`;
      window.history.pushState({ egLoreCard: hash }, '', url);
    }
  }

  #closeAll() {
    ++this.#openRequest;
    for (const card of this.#cards) this.#closeCard(card);
  }

  /** @param {HTMLElement} except */
  async #closeAllExcept(except) {
    await Promise.all(this.#cards.filter((card) => card !== except).map((card) => this.#closeCard(card)));
  }

  /** @param {HTMLElement} card */
  async #closeCard(card) {
    const host = card.querySelector('dialog-component');
    const dialog = host?.querySelector('dialog');
    if (dialog?.open && typeof host.closeDialog === 'function') {
      await host.closeDialog();
    }
    card.querySelector('[data-lv2-trigger]')?.setAttribute('aria-expanded', 'false');
  }

  /**
   * Fired by <dialog-component> on ESC / backdrop / close-button close.
   * @param {HTMLElement} card
   */
  #handleClose(card) {
    card.querySelector('[data-lv2-trigger]')?.setAttribute('aria-expanded', 'false');

    try {
      const current = decodeURIComponent(window.location.hash.slice(1));
      if (current && current === card.dataset.lv2Hash) {
        window.history.pushState({}, '', `${window.location.pathname}${window.location.search}`);
      }
    } catch {
      /* malformed hash; leave it alone */
    }
  }

  #initPointerGlow() {
    if (this.#pointerCleanup) {
      this.#pointerCleanup();
      this.#pointerCleanup = null;
    }
    if (!this.querySelector('.eg-lv2__pointer')) return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches || reducedMotion()) return;

    let frame = 0;
    let clientX = 0;
    let clientY = 0;

    const move = (event) => {
      clientX = event.clientX;
      clientY = event.clientY;
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const rect = this.getBoundingClientRect();
        this.style.setProperty('--eg-lv2-x', `${Math.round(clientX - rect.left)}px`);
        this.style.setProperty('--eg-lv2-y', `${Math.round(clientY - rect.top)}px`);
        this.dataset.pointer = '';
      });
    };
    const leave = () => {
      delete this.dataset.pointer;
    };

    this.addEventListener('pointermove', move, { passive: true });
    this.addEventListener('pointerleave', leave);
    this.#pointerCleanup = () => {
      this.removeEventListener('pointermove', move);
      this.removeEventListener('pointerleave', leave);
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
    };
  }
}

if (!window.customElements.get('eg-lore-detail-v2')) {
  window.customElements.define('eg-lore-detail-v2', EgLoreDetailV2);
}
